import { useCallback, useEffect, useRef, useState } from "react"

import { showInfoToast } from "@fuse/sharedComponents/themeToast/toastUtils"
import { debounce } from "lodash-es"

const getTheLocationInfo = async ({
  latitude,
  longitude,
  getFullAddress = false,
}) => {
  // const response = await fetch(
  //   `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`,
  // )
  if (!latitude || !longitude) return ""
  try {
    const response = await fetch(
      `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=${import.meta.env.VITE_GOOGLE_API_KEY}&callback=Function.prototype`,
    )
    const data = await response.json()
    const address_components = data?.results[0]?.address_components
    let areaName = ""
    for (let i = 0; i < address_components.length; i++) {
      const component = address_components[i]
      if (
        component.types.includes("locality") ||
        component.types.includes("political")
      ) {
        areaName = getFullAddress
          ? data?.results[0]?.formatted_address
          : component.long_name
        break
      }
    }
    return (
      areaName ||
      data?.results[0]?.formatted_address ||
      data?.plus_code?.compound_code
    )
  } catch (err) {
    console.warn(err)
    return ""
  }
}

const getThePreciseLocation = (calbk, getFullAddress = false) => {
  if (navigator?.geolocation) {
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const name = await getTheLocationInfo({
          latitude: position?.coords?.latitude,
          longitude: position?.coords?.longitude,
          getFullAddress,
        })

        calbk({
          location: {
            lat: position.coords.latitude,
            lon: position.coords.longitude,
            nameOfPlace: name,
          },
        })
      },
      (err) => {
        console.error("Location Error:", err)
        showInfoToast(handleLocationError(err))
        // fetchIpAndInfo(calbk)
      },
      {
        enableHighAccuracy: true, // Use GPS if available
        timeout: 10000, // Wait 10s before failing
        maximumAge: 0, // No cached locations
      },
    )
  } else {
    console.log("Geolocation is not supported by this browser.")
    showInfoToast("Geolocation is not supported by this browser.")
    // fetchIpAndInfo(calbk)
  }
}

const handleLocationError = (error) => {
  switch (error.code) {
    case error.PERMISSION_DENIED:
      return "User denied the request for Geolocation."
    case error.POSITION_UNAVAILABLE:
      return "Location information is unavailable."
    case error.TIMEOUT:
      return "The request to get user location timed out."
    case error.UNKNOWN_ERROR:
      return "An unknown error occurred."
    default:
      return "An error occurred while fetching location."
  }
}

const fetchIpAndInfo = async (calbk) => {
  fetch("https://ipinfo.io/json")
    .then((response) => response.json())
    .then((data) => {
      const { city, loc } = data
      const [lat, lon] = loc.split(",")
      calbk({
        location: {
          lat,
          lon,
          nameOfPlace: city,
        },
      })
    })
    .catch((error) => {
      console.error("Error fetching IP address:", error)
    })
}

const loadGoogleMapsScript = () => {
  if (typeof window.google !== "undefined" && window.google.maps) {
    return Promise.resolve()
  }

  return new Promise<void>((resolve, reject) => {
    const script = document.createElement("script")
    script.src = `https://maps.googleapis.com/maps/api/js?key=${import.meta.env.VITE_GOOGLE_API_KEY}&libraries=places&callback=Function.prototype`
    script.async = true
    script.defer = true

    script.onload = () => resolve()
    script.onerror = () =>
      reject(new Error("Failed to load Google Maps script"))

    document.body.appendChild(script)
  })
}

const usePredictionAutocomplete = ({
  locTerm,
  isSessionRequired,
}: {
  locTerm: string
  isSessionRequired: boolean
}) => {
  const [result, setResult] = useState([])
  const handleLoadScript = useCallback(() => loadGoogleMapsScript(), [])
  const placesAutocompleteService = useRef(null)
  const autocompleteSession = useRef(null)
  const placesService = useRef(null)

  const fetchThePredictionList = useCallback(
    async (term: string) => {
      if (placesAutocompleteService.current && term) {
        const result = await placesAutocompleteService.current.getListForTerm({
          input: term,
          language: "en",
          ...(isSessionRequired && autocompleteSession.current
            ? { sessionToken: autocompleteSession.current }
            : {}),
        })

        if (result?.suggestions?.length) {
          const trandformedResult = result?.suggestions.map((i) => {
            return {
              description: i.placePrediction.text.text,
              place_id: i.placePrediction.placeId,
              types: i.placePrediction.types,
            }
          })

          setResult(trandformedResult)
        } else {
          console.error("Error getting the prediction list:", result)
        }
      }
    },
    [isSessionRequired],
  )

  const fetchPlaceDetails = useCallback(
    async (placeId: string, getFullAddress = false): Promise<any> => {
      if (!placesService.current)
        return Promise.reject("Service is not initialized")

      try {
        const fields = [
          "addressComponents",
          "adrFormatAddress",
          "formattedAddress",
          "location",
          "displayName",
          "id",
          "plusCode",
          "types",
          "googleMapsURI",
        ]

        const place = await placesService.current.getPlaceDetails({
          placeId,
          fields,
          ...(isSessionRequired && autocompleteSession.current
            ? { sessionToken: autocompleteSession.current }
            : {}),
        })

        // Check if location exists
        if (place?.location) {
          const addressComponents = place.addressComponents
          let areaName = ""

          // Extract area name from address components
          if (addressComponents) {
            for (let i = 0; i < addressComponents.length; i++) {
              const component = addressComponents[i]
              if (
                component.types.includes("locality") ||
                component.types.includes("political")
              ) {
                areaName = component.longText
                break
              }
            }
          }

          // Fallback to place name or formatted address
          if (!areaName) {
            areaName = place?.displayName || place?.formattedAddress
          }

          // Refresh session token to prevent reuse
          if (autocompleteSession.current) {
            autocompleteSession.current =
              new google.maps.places.AutocompleteSessionToken()
          }

          return Promise.resolve({
            location: {
              lat: place.location.lat(),
              lon: place.location.lng(),
              nameOfPlace: getFullAddress ? place?.formattedAddress : areaName,
            },
          })
        } else {
          console.error("Error: Place location not found")
          return Promise.reject("Place location not found")
        }
      } catch (error: unknown) {
        // Handle the error with proper type checking
        let errorReason = "Unknown error"

        if (error && typeof error === "object" && "code" in error) {
          const errorCode = (error as { code: string }).code
          const errorMap: Record<string, string> = {
            INVALID_REQUEST: "Invalid request - check Place ID and fields.",
            OVER_QUERY_LIMIT: "Quota exceeded - too many requests.",
            REQUEST_DENIED: "Request denied - check API key and permissions.",
            UNKNOWN_ERROR: "Unknown error - retry may succeed.",
            ZERO_RESULTS: "No results found for this place ID.",
            NOT_FOUND: "Place ID not found.",
          }

          errorReason = errorMap[errorCode] || `Error: ${errorCode}`
        } else if (error && typeof error === "object" && "message" in error) {
          errorReason = (error as { message: string }).message
        } else if (error instanceof Error) {
          errorReason = error.message
        }

        console.error("Error fetching place details:", error, errorReason)
        return Promise.reject(errorReason)
      }
    },
    [],
  )

  const debounceLocationSearchVal = useCallback(
    debounce(fetchThePredictionList, 300),
    [fetchThePredictionList],
  )

  const builder = () => {
    if (!window.google || !window.google.maps || !window.google.maps.places)
      return console.error(
        "Google has not been found. Make sure your provide apiKey prop.",
      )

    placesAutocompleteService.current = {
      async getListForTerm({
        input,
        language,
        ...rest
      }: {
        input: string
        language: string
      }) {
        let result = {}
        try {
          result =
            await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(
              {
                input,
                language,
                ...rest,
              },
            )
        } catch (err) {
          console.error("ERROR:", err) //
        }

        return result
      },
    }

    placesService.current = {
      async getPlaceDetails({
        placeId,
        fields,
        ...rest
      }: {
        placeId: string
        fields: google.maps.places.FetchFieldsRequest
      }) {
        let place = null
        try {
          place = new google.maps.places.Place({
            id: placeId,
            requestedLanguage: "en",
            ...rest,
          })
          await place.fetchFields({ fields })
        } catch (err) {
          console.error("Error:", err)
        }

        return place
      },
    }

    if (isSessionRequired)
      autocompleteSession.current =
        new window.google.maps.places.AutocompleteSessionToken()
  }

  useEffect(() => {
    // if (import.meta.env.VITE_GOOGLE_API_KEY) {
    //   handleLoadScript().then(() => builder())
    // } else {
    //   builder()
    // }

    if (typeof window.google !== "undefined") {
      builder()
    } else {
      handleLoadScript().then(() => builder())
    }
  }, [])

  useEffect(() => {
    debounceLocationSearchVal(locTerm)
  }, [locTerm])

  return { predictionList: result, fetchPlaceDetails }
}

export {
  fetchIpAndInfo,
  getTheLocationInfo,
  getThePreciseLocation,
  loadGoogleMapsScript,
  usePredictionAutocomplete,
}
